import { useState } from 'react';

import type { DocumentSource } from '@/components/feedback/documentSource';

import type { OutstandingItem } from './outstanding.schema';
import type { OutstandingAction } from './rowActions';

/**
 * Which dialog is open.
 *
 * `document` joined the three obligation dialogs when viewing stopped being a
 * navigation. A link action used to leave the portal — the file was served as
 * an attachment, so following it downloaded rather than displayed — and now
 * opens a reader over the screen instead, which is state that has to be held
 * like any other dialog's.
 */
export type OpenDialog =
  | { kind: 'pay' | 'sign' | 'invite'; item: OutstandingItem }
  | { kind: 'document'; source: DocumentSource; title: string }
  | undefined;

export interface ItemDialogsState {
  open: (item: OutstandingItem, action: OutstandingAction) => void;
  current: OpenDialog;
  close: () => void;
}

/**
 * Which of the item dialogs is open, if any.
 *
 * Shared because every surface offering Pay / Sign / Invite needs exactly this
 * branching — the dashboard, the Changes tab and the Annual Reviews tab would
 * otherwise each carry a copy, which is how the legacy portal ended up with
 * three payment dialogs that had drifted apart.
 */
export const useItemDialogs = (): ItemDialogsState => {
  const [current, setCurrent] = useState<OpenDialog>(undefined);

  return {
    current,
    open: (item, action) => {
      if (action.kind === 'pay' || action.kind === 'sign' || action.kind === 'invite') {
        setCurrent({ kind: action.kind, item });
        return;
      }

      // Every remaining kind is a link action, and `href` is what distinguishes
      // one with a file from the disabled "File not available" placeholder.
      if (action.href !== undefined && action.href !== '') {
        setCurrent({
          kind: 'document',
          source: { url: action.href },
          title: action.label,
        });
      }
    },
    close: () => {
      setCurrent(undefined);
    },
  };
};
